import { all, takeEvery, fork, call, put } from 'redux-saga/effects';
import axios from 'axios';
import isFunction from 'lodash/isFunction';

import actions from './actions';
import { handleError } from '../../errorHandler';
import { API_HOST } from '../../constants';
import _ from 'lodash';

const MEMBER_POINT_API_URL = `${API_HOST}/api/memberPoint`;
const MEMBER_POINT_THIRD_PARTY_API_URL = `${API_HOST}/api/memberPoint/thirdParty`;
const MEMBER_POINT_ALLOCATION_RULE_API_URL = `${API_HOST}/api/memberPoint/rule/allocation`;
const MEMBER_POINT_DEDUCTION_RULE_API_URL = `${API_HOST}/api/memberPoint/rule/deduction`;
const MEMBER_POINT_TRANSACTION_RECORD_API_URL = `${API_HOST}/api/memberPoint/record/transaction`;
const MEMBER_POINT_THIRD_PARTY_TRANSACTION_RECORD_API_URL = `${API_HOST}/api/memberPoint/record/thirdParty/transaction`;
const MEMBER_POINT_EXCHANGE_API_URL = `${API_HOST}/api/memberPoint/exchangeRule`;
const MANUAL_RECALCULATION_URL = `${MEMBER_POINT_API_URL}/manualRecalculation`;

function* runManualPointRecalculation() {
  yield takeEvery(actions.RUN_MANUAL_POINT_RECALCULATION, function* (action) {
    const { formData, onResult, onError } = action;
    try {
      const res = yield call([axios, 'post'], MANUAL_RECALCULATION_URL, formData, { timeout: 130000 });
      if (isFunction(onResult)) yield call(onResult, res.data.data);
    } catch (error) {
      // The request may have committed after the browser timed out. Read the
      // original operation before offering another attempt.
      try {
        const status = yield call([axios, 'get'], `${MANUAL_RECALCULATION_URL}/request/${formData.request_key}`);
        if (isFunction(onResult) && status.data.data &&
            status.data.data.member_id === formData.member_id &&
            status.data.data.member_point_type_id === formData.member_point_type_id) {
          yield call(onResult, status.data.data);
          return;
        }
      } catch (statusError) {
        // Preserve the unknown outcome for the caller.
      }
      if (isFunction(onError)) yield call(onError, error);
    }
  });
}

function* getActiveManualPointRecalculation() {
  yield takeEvery(actions.GET_ACTIVE_MANUAL_POINT_RECALCULATION, function* (action) {
    const { memberId, pointTypeId, onResult } = action;
    try {
      const res = yield call([axios, 'get'], `${MANUAL_RECALCULATION_URL}/active/${memberId}/${pointTypeId}`);
      if (isFunction(onResult)) yield call(onResult, res.data.data);
    } catch (error) {
      // An unavailable status endpoint must not claim the operation succeeded.
    }
  });
}

function* getManualPointRecalculationByRequest() {
  yield takeEvery(actions.GET_MANUAL_POINT_RECALCULATION_BY_REQUEST, function* (action) {
    const { requestKey, onResult, onError } = action;
    try {
      const res = yield call([axios, 'get'], `${MANUAL_RECALCULATION_URL}/request/${requestKey}`);
      if (isFunction(onResult)) yield call(onResult, res.data.data);
    } catch (error) {
      if (isFunction(onError)) yield call(onError, error);
    }
  });
}

function* createMemberPointType() {
  yield takeEvery(actions.CREATE_MEMBER_POINT_TYPE, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'post'], MEMBER_POINT_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.CREATE_MEMBER_POINT_TYPE_ENDED });
    }
  });
}

function* createMemberPointThirdPartyType() {
  yield takeEvery(actions.CREATE_MEMBER_POINT_THIRD_PARTY_TYPE, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'post'], MEMBER_POINT_THIRD_PARTY_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.CREATE_MEMBER_POINT_THIRD_PARTY_TYPE_ENDED });
    }
  });
}

function* createMemberPointAllocationRule() {
  yield takeEvery(actions.CREATE_MEMBER_POINT_ALLOCATION_RULE, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'post'], MEMBER_POINT_ALLOCATION_RULE_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.CREATE_MEMBER_POINT_ALLOCATION_RULE_ENDED });
    }
  });
}

function* createMemberPointDeductionRule() {
  yield takeEvery(actions.CREATE_MEMBER_POINT_DEDUCTION_RULE, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'post'], MEMBER_POINT_DEDUCTION_RULE_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.CREATE_MEMBER_POINT_DEDUCTION_RULE_ENDED });
    }
  });
}

function* allocatePointsToMember() {
  yield takeEvery(actions.ALLOCATE_POINTS_TO_MEMBER, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'post'], `${MEMBER_POINT_API_URL}/allocation`, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.ALLOCATE_POINTS_TO_MEMBER_ENDED });
    }
  });
}

function* deductPointsFromMember() {
  yield takeEvery(actions.DEDUCT_POINTS_FROM_MEMBER, function* (action) {
    const { formData, onSuccess, onNeedConfirmation, onFinished } = action;
    try {
      const res = yield call([axios, 'post'], `${MEMBER_POINT_API_URL}/deduction`, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      if (error.code === '0x00150004' && isFunction(onNeedConfirmation)) {
        yield call(onNeedConfirmation);
      } else {
        yield handleError(error);
      }
    } finally {
      if (isFunction(onFinished)) yield call(onFinished);
      yield put({ type: actions.DEDUCT_POINTS_FROM_MEMBER_ENDED });
    }
  });
}

function* deductPointsForDiscountFromMember() {
  yield takeEvery(actions.DEDUCT_POINTS_FOR_DISCOUNT_FROM_MEMBER, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'post'],
        `${MEMBER_POINT_API_URL}/discountDeduction`,
        formData,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.DEDUCT_POINTS_FOR_DISCOUNT_FROM_MEMBER_ENDED });
    }
  });
}

function* searchMemberPointTypes() {
  yield takeEvery(actions.SEARCH_MEMBER_POINT_TYPES, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call([axios, 'get'], `${MEMBER_POINT_API_URL}?${parameters.toString()}`);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_MEMBER_POINT_TYPES_ENDED });
    }
  });
}

function* searchMemberPointThirdPartyTypes() {
  yield takeEvery(actions.SEARCH_MEMBER_POINT_THIRD_PARTY_TYPES, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${MEMBER_POINT_THIRD_PARTY_API_URL}?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_MEMBER_POINT_THIRD_PARTY_TYPES_ENDED });
    }
  });
}

function* searchMemberPointAllocationRules() {
  yield takeEvery(actions.SEARCH_MEMBER_POINT_ALLOCATION_RULES, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${MEMBER_POINT_ALLOCATION_RULE_API_URL}?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_MEMBER_POINT_ALLOCATION_RULES_ENDED });
    }
  });
}

function* searchMemberPointDeductionRules() {
  yield takeEvery(actions.SEARCH_MEMBER_POINT_DEDUCTION_RULES, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${MEMBER_POINT_DEDUCTION_RULE_API_URL}?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_MEMBER_POINT_DEDUCTION_RULES_ENDED });
    }
  });
}

function* searchMemberPointTransactionRecords() {
  yield takeEvery(actions.SEARCH_MEMBER_POINT_TRANSACTION_RECORDS, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${MEMBER_POINT_TRANSACTION_RECORD_API_URL}?${parameters.toString()}`,
        { timeout: 300000 }, // 5 minute
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_MEMBER_POINT_TRANSACTION_RECORDS_ENDED });
    }
  });
}

function* searchMemberThirdPartyPointTransactionRecords() {
  yield takeEvery(actions.SEARCH_MEMBER_THIRD_PARTY_POINT_TRANSACTION_RECORDS, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${MEMBER_POINT_THIRD_PARTY_TRANSACTION_RECORD_API_URL}?${parameters.toString()}`,
        { timeout: 300000 }, // 5 minute
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_MEMBER_THIRD_PARTY_POINT_TRANSACTION_RECORDS_ENDED });
    }
  });
}

function* findMemberPointTransactionRecord() {
  yield takeEvery(actions.FIND_MEMBER_POINT_TRANSACTION_RECORD, function* (action) {
    try {
      const { id, onSuccess } = action;
      const res = yield call([axios, 'get'], `${MEMBER_POINT_TRANSACTION_RECORD_API_URL}/${id}`);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.FIND_MEMBER_POINT_TRANSACTION_RECORD_ENDED });
    }
  });
}

function* findMemberThirdPartyPointTransactionRecord() {
  yield takeEvery(actions.FIND_MEMBER_THIRD_PARTY_POINT_TRANSACTION_RECORD, function* (action) {
    try {
      const { id, onSuccess } = action;
      const res = yield call(
        [axios, 'get'],
        `${MEMBER_POINT_THIRD_PARTY_TRANSACTION_RECORD_API_URL}/${id}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.FIND_MEMBER_POINT_TRANSACTION_RECORD_ENDED });
    }
  });
}

function* getEnabledMemberPointTypes() {
  yield takeEvery(actions.GET_ENABLED_MEMBER_POINT_TYPES, function* (action) {
    try {
      const { onSuccess } = action;
      const parameters = new URLSearchParams({
        enabled: 1,
        page: 1,
        number_per_page: 100,
        sort_by: 'updated_at',
        order: 'asc',
      });
      const res = yield call([axios, 'get'], `${MEMBER_POINT_API_URL}?${parameters.toString()}`);
      yield put({
        type: actions.GET_ENABLED_MEMBER_POINT_TYPES_ENDED,
        pointTypes: res.data.data,
      });
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
      yield put({ type: actions.GET_ENABLED_MEMBER_POINT_TYPES_ENDED });
    }
  });
}

function* getEnabledMemberPointThirdPartyTypes() {
  yield takeEvery(actions.GET_ENABLED_MEMBER_POINT_THIRD_PARTY_TYPES, function* (action) {
    try {
      const { onSuccess } = action;
      const parameters = new URLSearchParams({
        enabled: 1,
        page: 1,
        number_per_page: 100,
        sort_by: 'updated_at',
        order: 'asc',
      });
      const res = yield call(
        [axios, 'get'],
        `${MEMBER_POINT_THIRD_PARTY_API_URL}?${parameters.toString()}`,
      );
      yield put({
        type: actions.GET_ENABLED_MEMBER_POINT_THIRD_PARTY_TYPES_ENDED,
        pointTypes: res.data.data,
      });
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
      yield put({ type: actions.GET_ENABLED_MEMBER_POINT_THIRD_PARTY_TYPES_ENDED });
    }
  });
}

function* findMemberPointAllocationRule() {
  yield takeEvery(actions.FIND_MEMBER_POINT_ALLOCATION_RULE, function* (action) {
    try {
      const { id, onSuccess } = action;
      const res = yield call([axios, 'get'], `${MEMBER_POINT_ALLOCATION_RULE_API_URL}/${id}`);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.FIND_MEMBER_POINT_ALLOCATION_RULE_ENDED });
    }
  });
}

function* updateMemberPointType() {
  yield takeEvery(actions.UPDATE_MEMBER_POINT_TYPE, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'put'], MEMBER_POINT_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.UPDATE_MEMBER_POINT_TYPE_ENDED });
    }
  });
}

function* updateMemberPointThirdPartyType() {
  yield takeEvery(actions.UPDATE_MEMBER_POINT_THIRD_PARTY_TYPE, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'put'], MEMBER_POINT_THIRD_PARTY_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.UPDATE_MEMBER_POINT_THIRD_PARTY_TYPE_ENDED });
    }
  });
}

function* updateMemberPointAllocationRule() {
  yield takeEvery(actions.UPDATE_MEMBER_POINT_ALLOCATION_RULE, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'put'], MEMBER_POINT_ALLOCATION_RULE_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.UPDATE_MEMBER_POINT_ALLOCATION_RULE_ENDED });
    }
  });
}

function* updateMemberPointDeductionRule() {
  yield takeEvery(actions.UPDATE_MEMBER_POINT_DEDUCTION_RULE, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'put'], MEMBER_POINT_DEDUCTION_RULE_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.UPDATE_MEMBER_POINT_DEDUCTION_RULE_ENDED });
    }
  });
}

function* deleteMemberPointTypes() {
  yield takeEvery(actions.DELETE_MEMBER_POINT_TYPES, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'delete'], MEMBER_POINT_API_URL, { data: formData });
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.DELETE_MEMBER_POINT_TYPES_ENDED });
    }
  });
}

function* deleteMemberPointThirdPartyTypes() {
  yield takeEvery(actions.DELETE_MEMBER_POINT_THIRD_PARTY_TYPES, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'delete'], MEMBER_POINT_THIRD_PARTY_API_URL, {
        data: formData,
      });
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.DELETE_MEMBER_POINT_THIRD_PARTY_TYPES_ENDED });
    }
  });
}

function* deleteMemberPointAllocationRules() {
  yield takeEvery(actions.DELETE_MEMBER_POINT_ALLOCATION_RULES, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'delete'], MEMBER_POINT_ALLOCATION_RULE_API_URL, {
        data: formData,
      });
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.DELETE_MEMBER_POINT_ALLOCATION_RULES_ENDED });
    }
  });
}

function* deleteMemberPointDeductionRules() {
  yield takeEvery(actions.DELETE_MEMBER_POINT_DEDUCTION_RULES, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'delete'], MEMBER_POINT_DEDUCTION_RULE_API_URL, {
        data: formData,
      });
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.DELETE_MEMBER_POINT_DEDUCTION_RULES_ENDED });
    }
  });
}

function* copyMemberPointAllocationRule() {
  yield takeEvery(actions.COPY_MEMBER_POINT_ALLOCATION_RULE, function* (action) {
    const { id, onSuccess } = action;
    try {
      const res_get = yield call(
        [axios, 'get'],
        `${API_HOST}/api/memberPoint/rule/allocation/${id}`,
      );
      if (isFunction(onSuccess)) {
        res_get.data.data.name = _.get(res_get.data.data.rule, 'name');
        res_get.data.data.member_point_type_id = _.get(
          res_get.data.data.rule,
          'member_point_type_id',
        );
        res_get.data.data.valid_start_at = _.get(res_get.data.data.rule, 'valid_start_at');
        res_get.data.data.valid_end_at = _.get(res_get.data.data.rule, 'valid_end_at');
        res_get.data.data.unit = _.get(res_get.data.data, 'unit');
        res_get.data.data.unit_base = _.get(res_get.data.data, 'unit_base');
        res_get.data.data.point_unit_base = _.get(res_get.data.data, 'point_unit_base');
        res_get.data.data.prior_transaction_filter_key_values = _.get(
          res_get.data.data,
          'applicable_prior_transaction_key_values',
        );
        res_get.data.data.applicable_store_ids = _.map(res_get.data.data.applicable_stores, 'id');
        res_get.data.data.applicable_brand_ids = _.map(res_get.data.data.applicable_brands, 'id');
        res_get.data.data.applicable_product_ids = _.map(
          res_get.data.data.applicable_products,
          'id',
        );
        res_get.data.data.applicable_product_category_ids = _.map(
          res_get.data.data.applicable_product_categories,
          'id',
        );
        res_get.data.data.applicable_membership_level_ids = _.map(
          res_get.data.data.applicable_membership_levels,
          'id',
        );
        res_get.data.data.point_expiration_date_time = _.get(
          res_get.data.data,
          'point_expiration_date_time',
        );
        res_get.data.data.point_expiration_type = _.get(res_get.data.data, 'point_expiration_type');
        res_get.data.data.valid_periodicity_type = _.get(
          res_get.data.data,
          'valid_periodicity_type',
        );
        delete res_get.data.data.rule;
        delete res_get.data.data.applicable_stores;
        delete res_get.data.data.applicable_brands;
        delete res_get.data.data.applicable_products;
        delete res_get.data.data.applicable_product_categories;
        delete res_get.data.data.applicable_membership_levels;

        const res_create = yield call(
          [axios, 'post'],
          `${API_HOST}/api/memberPoint/rule/allocation`,
          res_get.data.data,
        );
        if (isFunction(onSuccess)) {
          yield call(onSuccess, res_create.data);
        }
      }
    } catch (err) {
      yield handleError(err, 'inbox');
    } finally {
      yield put({ type: actions.COPY_MEMBER_POINT_ALLOCATION_RULE_END });
    }
  });
}

function* archiveAllocationRules() {
  yield takeEvery(actions.ARCHIVE_ALL_MEMBER_POINT_ALLOCATION_RULES, function* (action) {
    const { data, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'put'],
        `${API_HOST}/api/memberPoint/rule/allocation/archive`,
        data,
        { timeout: 900000 }, // 15 minute
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (err) {
      yield handleError(err, 'inbox', 'archiveAllocationRules');
    } finally {
      yield put({ type: actions.ARCHIVE_ALL_MEMBER_POINT_ALLOCATION_RULES_END });
    }
  });
}

function* searchMemberPointExchange() {
  yield takeEvery(actions.SEARCH_MEMBER_POINT_EXCHANGE, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${MEMBER_POINT_EXCHANGE_API_URL}?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_MEMBER_POINT_EXCHANGE_ENDED });
    }
  });
}

function* createMemberPointExchange() {
  yield takeEvery(actions.CREATE_MEMBER_POINT_EXCHANGE, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'post'], MEMBER_POINT_EXCHANGE_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.CREATE_MEMBER_POINT_EXCHANGE_ENDED });
    }
  });
}

function* updateMemberPointExchange() {
  yield takeEvery(actions.UPDATE_MEMBER_POINT_EXCHANGE, function* (action) {
    const { formData, onSuccess, id } = action;
    try {
      const res = yield call([axios, 'patch'], `${MEMBER_POINT_EXCHANGE_API_URL}/${id}`, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.UPDATE_MEMBER_POINT_EXCHANGE_ENDED });
    }
  });
}

function* deleteMemberPointExchange() {
  yield takeEvery(actions.DELETE_MEMBER_POINT_EXCHANGE, function* (action) {
    const { onSuccess, ids } = action;
    console.log(ids);
    try {
      const res = yield call([axios, 'delete'], MEMBER_POINT_EXCHANGE_API_URL, { data: { ids } });
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.DELETE_MEMBER_POINT_EXCHANGE_ENDED });
    }
  });
}

export default function* rootSaga() {
  yield all([
    fork(runManualPointRecalculation),
    fork(getActiveManualPointRecalculation),
    fork(getManualPointRecalculationByRequest),
    fork(createMemberPointType),
    fork(createMemberPointThirdPartyType),
    fork(createMemberPointAllocationRule),
    fork(createMemberPointDeductionRule),
    fork(allocatePointsToMember),
    fork(deductPointsFromMember),
    fork(deductPointsForDiscountFromMember),
    fork(searchMemberPointTypes),
    fork(searchMemberPointThirdPartyTypes),
    fork(searchMemberPointAllocationRules),
    fork(searchMemberPointDeductionRules),
    fork(searchMemberPointTransactionRecords),
    fork(searchMemberThirdPartyPointTransactionRecords),
    fork(findMemberPointTransactionRecord),
    fork(findMemberThirdPartyPointTransactionRecord),
    fork(getEnabledMemberPointTypes),
    fork(getEnabledMemberPointThirdPartyTypes),
    fork(findMemberPointAllocationRule),
    fork(updateMemberPointType),
    fork(updateMemberPointThirdPartyType),
    fork(updateMemberPointAllocationRule),
    fork(updateMemberPointDeductionRule),
    fork(deleteMemberPointTypes),
    fork(deleteMemberPointThirdPartyTypes),
    fork(deleteMemberPointAllocationRules),
    fork(deleteMemberPointDeductionRules),
    fork(copyMemberPointAllocationRule),
    fork(archiveAllocationRules),
    fork(searchMemberPointExchange),
    fork(createMemberPointExchange),
    fork(updateMemberPointExchange),
    fork(deleteMemberPointExchange),
  ]);
}
